import { useEffect, useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronDown } from 'lucide-react'

import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Button } from '@/common/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Switch } from '@/common/ui/switch'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import ImageUploadModal from '@/common/components/ImageUploadModal'
import useMutationGetProfilePageBannerUploadUrl from '@/core/api/hooks/useMutationGetProfilePageBannerUploadUrl'
import { FileService } from '@/core/api/services/FileService'
import useQueryGetGameList from '@/modules/game/api/hooks/useQueryGetGameList'
import useQueryGetGameProviderList from '@/modules/game/api/hooks/useQueryGetGameProviderList'
import { ProfilePageBannerType } from '@/schema'

const formSchema = z
	.object({
		title: z.string().min(1, 'กรุณากรอกชื่อปุ่ม'),
		image: z.string().min(1, 'กรุณาอัปโหลดรูปไอคอน'),
		type: z.nativeEnum(ProfilePageBannerType),
		link: z.string().optional(),
		gameCode: z.string().optional(),
		order: z.number().min(0, 'ลำดับต้องมากกว่าหรือเท่ากับ 0').optional(),
		isActive: z.boolean().default(true),
	})
	.refine(
		(data) => {
			if (data.type === 'LINK' && !data.link) {
				return false
			}
			if (data.type === 'GAME' && !data.gameCode) {
				return false
			}
			return true
		},
		{
			message: 'กรุณากรอกข้อมูลให้ครบถ้วน',
			path: ['type'],
		},
	)

export type ProfilePageBannerFormValues = z.infer<typeof formSchema>

interface ProfilePageBannerFormProps {
	defaultValues?: Partial<ProfilePageBannerFormValues>
	onSubmit: (data: ProfilePageBannerFormValues) => void
	submitButtonText: string
	showOrder?: boolean
	showIsActive?: boolean
}

export default function ProfilePageBannerForm({
	defaultValues = {
		title: '',
		image: '',
		type: ProfilePageBannerType.LINK,
		link: '',
		gameCode: '',
		order: 0,
		isActive: true,
	},
	onSubmit,
	submitButtonText,
	showOrder = false,
	showIsActive = true,
}: ProfilePageBannerFormProps) {
	const [uploadedImageUrl, setUploadedImageUrl] = useState<string>('')
	const [gameProviderSearch, setGameProviderSearch] = useState('')
	const [gameSearch, setGameSearch] = useState('')
	const [selectedGameProviderId, setSelectedGameProviderId] = useState<
		string | undefined
	>(undefined)

	const form = useForm<ProfilePageBannerFormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: defaultValues as ProfilePageBannerFormValues,
	})

	const { mutateAsync: getPresignedUrl } =
		useMutationGetProfilePageBannerUploadUrl()

	const watchedType = form.watch('type')
	const watchedGameCode = form.watch('gameCode')

	// Fetch game providers
	const { data: gameProvidersData } = useQueryGetGameProviderList({
		skip: 0,
		take: 1000,
	})

	const payload = {
		skip: 0,
		take: 1000,
		providerName: selectedGameProviderId,
		name: gameSearch,
	}

	// Fetch games (filtered by provider if selected)
	const { data: gamesData } = useQueryGetGameList(payload)

	// Filter game providers by search
	const filteredGameProviders = useMemo(() => {
		if (!gameProvidersData?.data) return []
		if (!gameProviderSearch) return gameProvidersData.data
		return gameProvidersData.data.filter((provider) =>
			provider.name.toLowerCase().includes(gameProviderSearch.toLowerCase()),
		)
	}, [gameProvidersData, gameProviderSearch])

	// Filter games by search
	const filteredGames = useMemo(() => {
		if (!gamesData?.data) return []
		if (!gameSearch) return gamesData.data
		return gamesData.data.filter((game) => {
			const displayName = game.displayName || game.name
			return displayName.toLowerCase().includes(gameSearch.toLowerCase())
		})
	}, [gamesData, gameSearch])

	// Get selected game provider name
	const selectedGameProvider = useMemo(
		() =>
			gameProvidersData?.data?.find(
				(provider) => provider.name === selectedGameProviderId,
			),
		[gameProvidersData, selectedGameProviderId],
	)

	// Get selected game
	const selectedGame = useMemo(
		() => gamesData?.data?.find((game) => game.code === watchedGameCode),
		[gamesData, watchedGameCode],
	)

	// Populate form with initial data when available
	useEffect(() => {
		if (defaultValues && defaultValues.image) {
			setUploadedImageUrl(defaultValues.image)
		}
	}, [defaultValues])

	// Handle image cropping and upload
	const handleImageCropped = async (imageBlob: Blob) => {
		try {
			// Check if the blob is a GIF - upload directly without conversion
			if (imageBlob.type === 'image/gif') {
				// Get presigned URL for GIF
				const urlData = await getPresignedUrl({ fileExtension: 'gif' })

				if (!urlData.presignedUrl || !urlData.fileUrl) {
					throw new Error('Failed to get upload URL')
				}

				// Upload GIF directly to presigned URL (preserves animation)
				const gifFile = new File([imageBlob], `image_${Date.now()}.gif`, {
					type: 'image/gif',
				})
				await FileService.uploadFileToPresignedUrl(
					urlData.presignedUrl,
					gifFile,
				)

				// Update form with the file URL
				const imageUrl = urlData.fileUrl
				form.setValue('image', imageUrl)
				setUploadedImageUrl(imageUrl)

				toast.success('อัปโหลด GIF สำเร็จ')
			} else {
				// Convert blob to WebP File with 75% quality for other image types
				const webpFile = await convertBlobToWebP(imageBlob, 0.75)

				// Get presigned URL for WebP
				const urlData = await getPresignedUrl({ fileExtension: 'webp' })

				if (!urlData.presignedUrl || !urlData.fileUrl) {
					throw new Error('Failed to get upload URL')
				}

				// Upload WebP file to presigned URL
				await FileService.uploadFileToPresignedUrl(
					urlData.presignedUrl,
					webpFile,
				)

				// Update form with the file URL
				const imageUrl = urlData.fileUrl
				form.setValue('image', imageUrl)
				setUploadedImageUrl(imageUrl)

				toast.success('รูปไอคอนถูกอัปโหลดเรียบร้อยแล้ว')
			}
		} catch (error) {
			console.error('Upload failed:', error)
			showErrorToast('ไม่สามารถอัปโหลดรูปไอคอนได้ กรุณาลองใหม่อีกครั้ง')
			throw error
		}
	}

	// Convert blob to WebP format with specified quality
	const convertBlobToWebP = (
		blob: Blob,
		quality: number = 0.75,
	): Promise<File> => {
		return new Promise((resolve, reject) => {
			const canvas = document.createElement('canvas')
			const ctx = canvas.getContext('2d')
			const img = new Image()

			if (!ctx) {
				reject(new Error('Could not get canvas context'))
				return
			}

			img.onload = () => {
				canvas.width = img.width
				canvas.height = img.height
				ctx.drawImage(img, 0, 0)

				canvas.toBlob(
					(webpBlob) => {
						if (webpBlob) {
							const webpFile = new File([webpBlob], 'icon.webp', {
								type: 'image/webp',
							})
							resolve(webpFile)
						} else {
							reject(new Error('Failed to convert to WebP'))
						}
					},
					'image/webp',
					quality,
				)
			}

			img.onerror = () => reject(new Error('Failed to load image'))
			img.src = URL.createObjectURL(blob)
		})
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<FormField
					control={form.control}
					name="title"
					render={({ field }) => (
						<FormItem>
							<FormLabel>ชื่อปุ่ม</FormLabel>
							<FormControl>
								<Input placeholder="ใส่ชื่อปุ่ม" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="image"
					render={({ field }) => (
						<FormItem>
							<FormControl>
								<ImageUploadModal
									label="ไอคอนปุ่ม"
									buttonText="อัปโหลดไอคอน"
									aspectRatio={1}
									maxFileSize={5 * 1024 * 1024}
									acceptedFileTypes={[
										'image/jpeg',
										'image/png',
										'image/webp',
										'image/gif',
									]}
									initialImageUrl={uploadedImageUrl || field.value}
									onImageCropped={handleImageCropped}
									required
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="type"
					render={({ field }) => (
						<FormItem>
							<FormLabel>ประเภท</FormLabel>
							<Select onValueChange={field.onChange} defaultValue={field.value}>
								<FormControl>
									<SelectTrigger>
										<SelectValue placeholder="เลือกประเภท" />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									<SelectItem value="LINK">ลิงก์</SelectItem>
									<SelectItem value="GAME">เกม</SelectItem>
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>

				{watchedType === 'LINK' && (
					<FormField
						control={form.control}
						name="link"
						render={({ field }) => (
							<FormItem>
								<FormLabel>URL ปลายทาง</FormLabel>
								<FormControl>
									<Input placeholder="https://example.com" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}

				{watchedType === 'GAME' && (
					<>
						{/* Game Provider Selector */}
						<div className="space-y-2">
							<FormLabel>ผู้ให้บริการเกม (ไม่บังคับ)</FormLabel>
							<Popover
								onOpenChange={(open) => {
									if (!open) {
										setGameProviderSearch('')
									}
								}}
							>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										role="combobox"
										type="button"
										className="w-full justify-between"
									>
										<span className="truncate">
											{selectedGameProvider
												? selectedGameProvider.name
												: 'ทั้งหมด'}
										</span>
										<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-full p-0" align="start">
									<div className="space-y-2 p-2">
										<Input
											placeholder="ค้นหาผู้ให้บริการเกม..."
											className="h-8 text-sm"
											value={gameProviderSearch}
											onChange={(e) => setGameProviderSearch(e.target.value)}
										/>
										<div className="max-h-[300px] space-y-1 overflow-y-auto">
											{/* Option to clear filter */}
											<div
												className={`flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent ${
													!selectedGameProviderId ? 'bg-accent' : ''
												}`}
												onClick={() => {
													setSelectedGameProviderId(undefined)
													form.setValue('gameCode', '')
												}}
											>
												<span className="truncate">ทั้งหมด</span>
											</div>
											{filteredGameProviders.length > 0 ? (
												filteredGameProviders.map((provider) => (
													<div
														key={provider.id}
														className={`flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent ${
															selectedGameProviderId === provider.name
																? 'bg-accent'
																: ''
														}`}
														onClick={() => {
															setSelectedGameProviderId(provider.name)
															form.setValue('gameCode', '')
														}}
													>
														<span className="truncate">{provider.name}</span>
													</div>
												))
											) : (
												<div className="py-2 text-center text-xs text-muted-foreground">
													ไม่พบผู้ให้บริการเกม
												</div>
											)}
										</div>
									</div>
								</PopoverContent>
							</Popover>
						</div>

						{/* Game Selector */}
						<FormField
							control={form.control}
							name="gameCode"
							render={({ field }) => (
								<FormItem>
									<FormLabel>เกม</FormLabel>
									<FormControl>
										<Popover
											onOpenChange={(open) => {
												if (!open) {
													setGameSearch('')
												}
											}}
										>
											<PopoverTrigger asChild>
												<Button
													variant="outline"
													role="combobox"
													type="button"
													className="w-full justify-between"
												>
													<span className="truncate">
														{selectedGame
															? selectedGame.displayName || selectedGame.name
															: 'เลือกเกม'}
													</span>
													<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
												</Button>
											</PopoverTrigger>
											<PopoverContent className="w-full p-0" align="start">
												<div className="space-y-2 p-2">
													<Input
														placeholder="ค้นหาเกม..."
														className="h-8 text-sm"
														value={gameSearch}
														onChange={(e) => setGameSearch(e.target.value)}
													/>
													<div className="max-h-[300px] space-y-1 overflow-y-auto">
														{filteredGames.length > 0 ? (
															filteredGames.map((game) => (
																<div
																	key={game.id}
																	className={`flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent ${
																		field.value === game.code ? 'bg-accent' : ''
																	}`}
																	onClick={() => {
																		field.onChange(game.code)
																	}}
																>
																	<span className="truncate">
																		{game.displayName || game.name}
																	</span>
																</div>
															))
														) : (
															<div className="py-2 text-center text-xs text-muted-foreground">
																{selectedGameProviderId
																	? 'ไม่พบเกมในผู้ให้บริการนี้'
																	: 'ไม่พบเกม'}
															</div>
														)}
													</div>
												</div>
											</PopoverContent>
										</Popover>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</>
				)}

				{showOrder && (
					<FormField
						control={form.control}
						name="order"
						render={({ field }) => (
							<FormItem>
								<FormLabel>ลำดับ</FormLabel>
								<FormControl>
									<Input
										type="number"
										min="0"
										{...field}
										onChange={(e) =>
											field.onChange(parseInt(e.target.value) || 0)
										}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}

				{showIsActive && (
					<FormField
						control={form.control}
						name="isActive"
						render={({ field }) => (
							<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
								<div className="space-y-0.5">
									<FormLabel className="text-base">โชว์ปุ่มบนหน้าฉัน</FormLabel>
								</div>
								<FormControl>
									<Switch
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
				)}

				<Button type="submit" className="w-full">
					{submitButtonText}
				</Button>
			</form>
		</Form>
	)
}
